import { ChangeEvent } from "react";
import { useSelector } from "react-redux";
import { RootState } from '../../redux/rootReducer';
import { InputField } from "../common";
import { NXAlertCircle } from "../../icons";

interface SEOTypes {
  seoData?: any;
  onChange: (e: any) => void;
}

const SEO = ({ seoData, onChange }: SEOTypes) => {
  const me = useSelector((state: RootState) => state.auth.me);

  const onChangeValue = (e: ChangeEvent<any>) => {
    onChange(e);
  };

  return (
    <div className="bg-white rounded-xl p-6">
      <div className="flex items-center space-x-3 mb-6">
        <h1 className="font-nunito font-[900] text-2xl text-gray-800">
          Search Engine Listing
        </h1>
        <NXAlertCircle className="text-primary-500" />
      </div>
      
      {/* SEO Preview */}
      <div className="py-4 mb-6">
        {seoData?.meta_title && seoData?.meta_description && seoData?.slug ? (
          <div className="bg-slate-50 p-5 rounded-lg border-l-4 border-primary-500 shadow-sm">
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">SEARCH PREVIEW</p>
            <div className="text-xl text-blue-600 font-semibold mb-1">
              {seoData?.meta_title}
            </div>
            <div className="text-sm text-green-600 mb-2">
              {me?.store_url + '/' + seoData?.slug}
            </div>
            <div className="text-sm text-gray-600">
              {seoData?.meta_description}
            </div>
          </div>
        ) : (
          <div className="bg-slate-50 p-5 rounded-lg border border-dashed border-gray-300 text-center">
            <p className="text-gray-400">Complete the fields below to see your search engine preview</p>
          </div>
        )}
      </div>
      
      {/* Input Fields */}
      <div className="space-y-6">
        <div>
          <div className="flex justify-between items-center mb-2">
            <label htmlFor="meta_title" className="font-nunito font-bold text-gray-700">
              Meta Title
            </label>
            {seoData?.meta_title && (
              <span className={`text-xs ${seoData?.meta_title.length > 60 ? 'text-red-500' : 'text-gray-400'}`}>
                {seoData?.meta_title.length}/60 characters
              </span>
            )}
          </div>
          <InputField
            id="meta_title"
            type="text"
            name="meta_title"
            placeholder="Enter a compelling title that summarizes your product"
            defaultValue={seoData?.meta_title}
            onChange={onChangeValue}
            className="w-full px-5 py-3 placeholder:text-gray-400 bg-secondary-50 rounded-xl font-nunito outline-[#0CAF60] border-[2px] border-dashed hover:border-primary-400 focus:border-solid"
            AIConfig={{fieldType: 'product_meta_title', fieldValue: seoData?.meta_title}}
          />
          <p className="mt-1 text-xs text-gray-500">Optimal length: 50-60 characters</p>
        </div>
        
        <div>
          <div className="flex justify-between items-center mb-2">
            <label htmlFor="meta_description" className="font-nunito font-bold text-gray-700">
              Meta Description
            </label>
            {seoData?.meta_description && (
              <span className={`text-xs ${seoData?.meta_description.length > 160 ? 'text-red-500' : 'text-gray-400'}`}>
                {seoData?.meta_description.length}/160 characters
              </span>
            )}
          </div>
          <InputField
            id="meta_description"
            type="text"
            name="meta_description"
            placeholder="Write a clear description highlighting key product features and benefits"
            defaultValue={seoData?.meta_description}
            onChange={onChangeValue}
            className="w-full px-5 py-3 placeholder:text-gray-400 bg-secondary-50 rounded-xl font-nunito outline-[#0CAF60] border-[2px] border-dashed hover:border-primary-400 focus:border-solid"
            AIConfig={{fieldType: 'product_meta_description', fieldValue: seoData?.meta_description}}
          />
          <p className="mt-1 text-xs text-gray-500">Optimal length: 150-160 characters</p>
        </div>
        
        <div>
          <label htmlFor="url" className="font-nunito font-bold text-gray-700 block mb-2">
            URL Slug
          </label>
          <div className="flex mt-1 py-3 px-5 bg-secondary-50 border-dashed border-2 rounded-xl hover:border-primary-400 focus-within:border-solid focus-within:border-[#0CAF60] transition-colors">
            <div className="text-gray-500 flex items-center border-r pr-2 mr-2 border-gray-200">
              {me?.store_url}/
            </div>
            <InputField
              id="url"
              type="text"
              name="slug"
              placeholder="product-name-keywords"
              defaultValue={seoData?.slug}
              onChange={onChangeValue}
              className="w-full ring-0 outline-none border-none focus:ring-0 focus:border-none focus:outline-none bg-transparent"
            /> 
          </div>
          <p className="mt-1 text-xs text-gray-500">Use hyphens to separate words. Avoid special characters.</p>
        </div>
      </div>
      
      {/* SEO Tips */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-primary-600 font-semibold mb-2 font-nunito">SEO Best Practices</h3>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>Include primary keywords early in your title and description</li>
          <li>Make your meta description compelling to increase click-through rates</li>
          <li>Keep URLs short, descriptive, and keyword-rich</li>
        </ul>
      </div>
    </div>
  );
};

export default SEO;